import React, { useEffect, useMemo, useState } from "react";

import type { ClientId } from "../../domain/types";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { ensureTallyRoundingOff } from "../../data/roundingOffApi";
import { tallyLedgerApi, type TallyChartLedger } from "../../data/tallyLedgerApi";
import type { RoundingOffAnswer } from "./RoundingOffStep";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { ComboPicker, type ComboOption } from "../../components/ComboPicker";
import { QuestionCard } from "../../components/QuestionCard";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

const ANSWER_KEY = "verifySetup.roundingOff";
const ROUND_OFF_NAME = "round off";

function isIndirectExpense(ledger: TallyChartLedger): boolean {
  return ledger.account_type === "indirect_expenses" && !ledger.is_group && ledger.destination === "gl";
}

function ledgerId(ledger: TallyChartLedger): string {
  return ledger.guid || ledger.account_id;
}

function isRoundOff(ledger: TallyChartLedger): boolean {
  return ledger.name.trim().toLowerCase() === ROUND_OFF_NAME;
}

/**
 * Tally side of Configure rounding off.
 *
 * Mapping records a ledger already under Indirect Expenses. Create queues
 * Round Off through the connector and polls until the chart read shows it.
 */
export function TallyRoundingOffStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const answer = useCoAnswer<RoundingOffAnswer>(clientId, ANSWER_KEY);
  const { goNext } = useSubStepNav(clientId, "rounding-off");

  const [ledgers, setLedgers] = useState<TallyChartLedger[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let live = true;
    tallyLedgerApi
      .chart(clientId)
      .then((chart) => {
        if (live) setLedgers(chart.ledgers);
      })
      .catch((cause: unknown) => {
        if (live) setLoadError(cause instanceof Error ? cause.message : "Could not read the chart of accounts.");
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  const expenses = useMemo(() => (ledgers ?? []).filter(isIndirectExpense), [ledgers]);
  const existingRoundOff = useMemo(() => expenses.find(isRoundOff) ?? null, [expenses]);
  const options = useMemo<ComboOption[]>(
    () =>
      expenses.map((ledger) => ({
        value: ledgerId(ledger),
        label: ledger.name,
        detail: ledger.parent_name ?? undefined,
        search: [ledger.name, ledger.parent_name ?? "", ledgerId(ledger)],
      })),
    [expenses]
  );

  const chosen = expenses.find((ledger) => ledgerId(ledger) === answer?.accountId) ?? null;
  const ready =
    answer?.mode === "created" ? Boolean(answer.accountId && answer.ledgerName) : Boolean(answer?.accountId) && chosen !== null;

  function save(next: RoundingOffAnswer) {
    setActionError(null);
    void persistAnswer<RoundingOffAnswer>(clientId, ANSWER_KEY, next);
  }

  function selectMode(mode: "mapped" | "created") {
    if (mode === "mapped") {
      save({
        mode,
        accountId: answer?.mode === "mapped" ? answer.accountId : undefined,
        ledgerName: answer?.mode === "mapped" ? answer.ledgerName : undefined,
      });
      return;
    }

    if (existingRoundOff) {
      save({
        mode,
        accountId: ledgerId(existingRoundOff),
        ledgerName: existingRoundOff.name,
      });
      return;
    }

    if (answer?.mode === "created" && answer.accountId) {
      return;
    }

    save({ mode });
  }

  useEffect(() => {
    if (answer?.mode !== "created" || !existingRoundOff || answer.accountId) return;
    save({
      mode: "created",
      accountId: ledgerId(existingRoundOff),
      ledgerName: existingRoundOff.name,
    });
  }, [answer?.accountId, answer?.mode, existingRoundOff]);

  useEffect(() => {
    if (!creating) return undefined;
    let live = true;
    const timer = window.setInterval(() => {
      ensureTallyRoundingOff(clientId)
        .then(async (result) => {
          if (!live) return;
          if (result.status === "pending") {
            setPendingMessage(result.message);
            return;
          }
          if (result.status === "failed" || !result.account_id || !result.ledger_name) {
            setCreating(false);
            setPendingMessage(null);
            setActionError(result.message || "Tally did not create Round Off.");
            return;
          }
          const chart = await tallyLedgerApi.chart(clientId);
          if (!live) return;
          setLedgers(chart.ledgers);
          save({
            mode: "created",
            accountId: result.account_id,
            ledgerName: result.ledger_name,
          });
          setPendingMessage(null);
          setCreating(false);
        })
        .catch((cause: unknown) => {
          if (!live) return;
          setCreating(false);
          setPendingMessage(null);
          setActionError(cause instanceof Error ? cause.message : "Could not create Round Off.");
        });
    }, 2000);

    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [creating, clientId]);

  async function createLedger(retry: boolean) {
    setCreating(true);
    setActionError(null);
    setPendingMessage("Creating Round Off under Indirect Expenses.");
    try {
      const result = await ensureTallyRoundingOff(clientId, retry);
      if (result.status === "pending") {
        setPendingMessage(result.message);
        return;
      }
      setCreating(false);
      setPendingMessage(null);
      if (result.status === "failed" || !result.account_id || !result.ledger_name) {
        setActionError(result.message || "Tally did not create Round Off.");
        return;
      }
      const chart = await tallyLedgerApi.chart(clientId);
      setLedgers(chart.ledgers);
      save({
        mode: "created",
        accountId: result.account_id,
        ledgerName: result.ledger_name,
      });
    } catch (cause: unknown) {
      setCreating(false);
      setPendingMessage(null);
      setActionError(cause instanceof Error ? cause.message : "Could not create Round Off.");
    }
  }

  if (!client) return null;

  const clientName = client.name || "this client";
  const missingSaved =
    answer?.mode === "mapped" && Boolean(answer.accountId) && ledgers !== null && chosen === null
      ? "The saved ledger is no longer under Indirect Expenses. Choose it again."
      : null;

  return (
    <SubStepShell
      title="Configure rounding off"
      description={
        <>
          Zournal posts small rounding differences to a single Round Off ledger in Tally. These come from invoices, bills, journal entries, and bank reconciliations, and posting them to one ledger keeps every entry balanced.
          <br />
          <br />
          Ledger: Round Off, under Indirect Expenses. This step adds it when the company does not already have it.
          <br />
          <br />
          You can replace it with another ledger under Indirect Expenses. Ledgers from other groups can&apos;t be selected.
        </>
      }
      thenWhat={ready ? "Continue → GST Settings." : "Choose the rounding-off ledger to continue."}
      onContinue={() => void goNext()}
      continueDisabled={!ready || creating}
    >
      {loadError && <Callout variant="warning">{loadError}</Callout>}
      {missingSaved && <Callout variant="warning">{missingSaved}</Callout>}
      {actionError && <Callout variant="warning">{actionError}</Callout>}

      <QuestionCard
        headline="Which ledger should take rounding off?"
        whyLine="Map one already under Indirect Expenses, or add Round Off there."
        options={[
          {
            value: "mapped",
            title: "Use an existing ledger",
            description: "Point at the Indirect Expenses ledger they already post rounding differences to. Nothing is created in Tally.",
          },
          {
            value: "created",
            title: "Create Round Off",
            description: "Add a ledger named Round Off under Indirect Expenses. If that name is already there, we use it.",
          },
        ]}
        selected={answer?.mode}
        onSelect={(value) => selectMode(value as "mapped" | "created")}
      />

      {answer?.mode === "mapped" && (
        <div className="flex flex-col gap-2">
          {ledgers === null ? (
            <p className="text-sm text-muted-foreground">Reading the chart of accounts…</p>
          ) : expenses.length === 0 ? (
            <Callout variant="warning">
              No ledgers under Indirect Expenses were read from {clientName}&apos;s company. Create Round Off, or refresh the chart on the previous step.
            </Callout>
          ) : (
            <ComboPicker
              options={options}
              value={answer?.accountId ?? ""}
              onChange={(accountId) => {
                const ledger = expenses.find((row) => ledgerId(row) === accountId);
                save({
                  mode: "mapped",
                  accountId,
                  ledgerName: ledger?.name,
                });
              }}
              placeholder="Select a ledger"
              searchPlaceholder="Search Indirect Expenses"
              noun="ledger"
            />
          )}
        </div>
      )}

      {answer?.mode === "created" && (
        <div className="flex flex-col gap-3">
          {answer.accountId ? (
            <Callout variant="success">
              {chosen?.name ?? answer.ledgerName ?? "Round Off"} will be used as the rounding-off ledger.
            </Callout>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                {pendingMessage ?? `This adds Round Off under Indirect Expenses in ${clientName}'s Tally company.`}
              </p>
              <CoButton onClick={() => void createLedger(Boolean(actionError))} disabled={creating || ledgers === null}>
                {creating ? "Adding…" : actionError ? "Try again" : "Create Round Off"}
              </CoButton>
            </>
          )}
        </div>
      )}
    </SubStepShell>
  );
}
